<template>
  <v-overlay :value="opened">
    <v-app :class="getClassname">
      <div v-if="step !== 3">
        <div class="share-modal-header">
          <v-spacer />
          <div class="share-modal-title">{{ getTitleByStep }}</div>
          <v-spacer />
          <div
            class="close-btn-wraper"
            @click="
              $emit('update-share', step === 3)
              resetFlow()
              $emit('toggleSharingModal')
            "
          >
            <v-icon
              style="margin: auto"
              class="fa-light fa-close"
              size="16"
              color="#E5E6EE"
            ></v-icon>
          </div>
        </div>
        <v-divider style="margin: 32px 0" />
      </div>
      <div style="display: flex; flex-direction: column" v-if="step === 1">
        <div v-if="documentToShare" class="document-to-share">
          {{ documentToShare.filename || documentToShare.description }}
        </div>
        <div v-else class="radio-group-wrapper">
          <v-radio-group v-model="initialValue" column @change="handleLinkCreation">
            <v-radio
              v-for="n in options"
              :key="n"
              :label="n.label"
              :value="n.value"
              class="radio-btn"
              color="#161616"
            ></v-radio>
          </v-radio-group>
        </div>
        <div class="guest-share-main-wrapper">
          <v-form v-model="valid" ref="form" class="guest-share-wrapper">
            <v-text-field
              v-model="email"
              outlined
              :rules="emailRules"
              placeholder="Enter an email to share with"
              required
              class="search-field"
              style="margin-right: 10px"
            ></v-text-field>
            <v-btn
              :color="valid ? '#128279' : '#C9CBD5'"
              style="
                color: #ffffff;
                margin-left: 10px;
                margin-bottom: 30px;
                height: 54px !important;
              "
              @click="initiateShare"
            >
              Share
            </v-btn>
          </v-form>
        </div>
        <v-divider style="margin: 40px 0" />
        <div class="share-text-main">
          <div v-if="!showSharingOptions" class="create-share-btn" @click="createShareLink">
            Create Share Link
          </div>
          <div class="copied-text" :class="{ show: displayNotification }">Copied to Clipboard!</div>
          <div v-if="shareLink">
            <div class="link-actions-container">
              <v-icon size="25" style="margin-right: 16px" @click="deleteShareLink">
                mdi-window-close</v-icon
              >
              <div class="link-wrapper">
                <div class="link-box-wrapper">
                  <div class="link-box" @click="copyLink">
                    {{ this.shareLink }}
                  </div>
                </div>
              </div>
            </div>
            <div class="copy-link" @click="copyLink" ref="copyLinkId">
              <v-spacer />
              <v-icon class="fa-light fa-link" size="20" color="#128279"></v-icon>
              <span class="copy-link-text">Copy link</span>
              <v-spacer />
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="confirm-share-wrapper">
          <div v-if="step === 3" class="done-icon-styling">
            <img :src="getIconURL" style="align-self: center" />
          </div>
          <div class="share-confirmation-text">
            {{ getTextByStep }}
            <div class="shareType-Styling" :style="styleColor">{{ formatResourceType }}</div>
            <div class="with-styling">with</div>
            <div class="share-values">{{ userEmail }}</div>
          </div>
          <div style="display: flex; margin: 32px 0">
            <div v-if="step === 2" class="cancel-btn-style" @click="resetFlow()">Cancel</div>
            <div v-if="step === 2" class="share-btn-style" @click="createShare">Share</div>
          </div>
        </div>
      </div>
      <div v-if="step === 3" :class="getBtnWrapperClassname">
        <div
          class="done-btn-style"
          @click="
            resetFlow()
            $emit('toggleSharingModal')
            $emit('update-share', true)
          "
        >
          Done
        </div>
      </div>
    </v-app>
  </v-overlay>
</template>

<script>
import get from 'lodash/get'
import { User, Shares, AppConfig } from '@ciitizen/api'
import { Cancer } from '@ciitizen/api'
import { Utils } from '../ciitizen-component-library'

export default {
  name: 'ShareModel',
  props: {
    opened: {
      type: Boolean,
      default: false
    },
    documentToShare: {
      type: Object,
      default: () => {}
    },
    userId: {
      type: String,
      default: ''
    },
    isExistingUser: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        resourceType: '',
        duration: '1y', // we will make each share valid for 1yr for now
        subject: null
      },
      valid: false,
      step: 1,
      userEmail: '',
      shareLink: '',
      showSharingOptions: false,
      showConfirmation: false,
      currentSharingId: '',
      currentResourceId: '',
      initialValue: '',
      email: '',
      emailRules: [
        v => !!v || 'E-mail is required',
        v => /.+@.+/.test(v) || 'E-mail must be valid'
      ],
      dialogVisible: false,
      selfShares: [],
      displayNotification: false,
      userProfile: {},
      hasCancerData: false
    }
  },
  computed: {
    styleColor() {
      return this.formatResourceType === 'Full Profile'
        ? 'color: #194066; background: #EEF3F8;'
        : this.shareType === 'Document'
        ? 'color: #323336; background: #E5E6EE;'
        : 'color: #8E2666; background: #F6EDF3;'
    },
    getIconURL() {
      return require('../statics/icons/checkmark.svg')
    },
    getTitleByStep() {
      switch (this.step) {
        case 1:
          return 'Share your Data'
        case 2:
          return 'Confirm your share'
      }
    },
    getResourceType() {
      if (this.documentToShare) {
        return 'Document'
      } else {
        return this.form.resourceType
      }
    },
    options() {
      let shareOptions = []
      if (this.hasCancerData) {
        shareOptions = [...shareOptions, { value: 'cc', label: 'Cancer Summary' }]
      }
      shareOptions = [...shareOptions, { value: '*', label: 'Full Profile' }]
      return shareOptions
    },
    getTextByStep() {
      return this.step === 2 ? 'Are you sure you want to share your' : 'Successfully shared your'
    },
    formatResourceType() {
      if (this.documentToShare) {
        return this.documentToShare.filename || this.documentToShare.description || 'Document'
      } else {
        return this.form.resourceType === '*' ? 'Full Profile' : 'Cancer Summary'
      }
    },
    getClassname() {
      const { isIphone } = Utils
      return isIphone() ? 'share-wrapper mt' : 'share-wrapper'
    },
    getBtnWrapperClassname() {
      const { isIphone } = Utils
      return isIphone() ? 'done-wrapper pb' : 'done-wrapper'
    }
  },
  methods: {
    initiateShare() {
      if (!this.$refs.form.validate()) return
      this.userEmail = this.email || this.form.subject
      this.step = 2
    },
    async fetchExistingShareLink() {
      let sharingResource = ''
      // check if a share exists and show link:
      for (let share of this.selfShares) {
        sharingResource = get(share, 'sharingResources[0]')
        if (!sharingResource.resourceId) {
          let shareDetails = await Shares.shareDetailWithId(share.sharingId)
          sharingResource = get(shareDetails, 'sharingResources[0]')
        }
        if (
          ['Document', 'ImagingStudy'].includes(this.getResourceType) &&
          sharingResource.resourceId === this.documentToShare.id
        ) {
          // this.shareLink = AppConfig + '/s/' + share.sharingId
          this.shareLink = this.getShareType(sharingResource.resourceType, share.sharingId)
          this.showSharingOptions = true
          this.currentSharingId = share.sharingId
          this.currentResourceId = sharingResource.id
          return
        } else if (
          !['Document', 'ImagingStudy'].includes(this.getResourceType) &&
          sharingResource.resourceType === this.getResourceType
        ) {
          // show the link
          // this.shareLink = AppConfig + '/s/' + share.sharingId
          this.shareLink = this.getShareType(sharingResource.resourceType, share.sharingId)
          this.showSharingOptions = true
          this.currentSharingId = share.sharingId
          this.currentResourceId = sharingResource.id
          return
        } else {
          this.shareLink = ''
          this.showSharingOptions = false
        }
      }
    },
    async handleModalOpening() {
      Utils.callSegmentTrack({ name: 'pp.share_modal.opened' })
      this.email = ''
      const data = await User.getUserProfile(this.userId)
      if (data) {
        this.userProfile = data.profile
      }
      this.form.resourceType = this.hasCancerData ? 'cc' : '*'
      this.initialValue = this.hasCancerData ? 'cc' : '*'
      const response = await Shares.loadUserShares()
      this.getSelfShares(response)
      this.fetchExistingShareLink()
    },
    getSelfShares(shares) {
      if (shares.sharingRecords) {
        this.selfShares = shares.sharingRecords.filter(share => share.sharingType === 'self_share')
      }
    },
    async deleteShareLink() {
      if (!this.currentResourceId) {
        let shareDetails = await Shares.shareDetailWithId(this.currentSharingId)
        this.currentResourceId = get(shareDetails, 'sharingResources[0].id')
      }
      await Shares.revokeSharedResourceId(this.currentSharingId, this.currentResourceId)
      this.showSharingOptions = false
      this.shareLink = ''
      Utils.callSegmentTrack({ name: 'pp.share_link.deleted' })
    },
    async copyLink() {
      if (navigator) {
        Utils.callSegmentTrack({ name: 'pp.share_link.copy' })
        await navigator.clipboard.writeText(this.shareLink).then(() => {
          this.displayNotification = true
        })
        setTimeout(() => {
          this.displayNotification = false
        }, 1500)
      }
    },
    async createShareLink() {
      let resource = ''
      if (this.documentToShare) {
        resource = this.documentToShare.filename || this.documentToShare.description
        resource = resource.replace(/\s+/g, '')
      } else {
        resource = this.form.resourceType === 'cc' ? 'summary' : 'everything'
      }
      let email = this.userProfile.email
      if (this.documentToShare || this.form.resourceType) {
        // generate email address based on sharing resource
        let index = email.indexOf('@')
        email = email.split('')
        email.splice(index, 0, `+${resource}`)
        email = email.join('')
      }
      let payload = {
        subject: email.toLowerCase(),
        duration: '1yr',
        sharingType: 'self_share', // only used for creating links
        emailNotify: false,
        meta: {}
      }
      if (this.documentToShare) {
        payload.id = this.documentToShare.id
        payload.resourceType = this.documentToShare.resourceType
        payload.meta.filename = this.documentToShare.filename || this.documentToShare.description
      } else {
        payload.resourceType = this.form.resourceType
      }
      // now get sharing id by creating a share
      try {
        Utils.callSegmentTrack({ name: 'pp.create_share_link.clicked' })
        let response = await this.callCreateSharesApi(payload)
        if (response) {
          let shareDetails = await Shares.shareDetailWithId(response.sharingId)
          this.currentResourceId = get(shareDetails, 'sharingResources[0].id')
          this.currentSharingId = response.sharingId
          let link = this.getShareType(payload.resourceType, response.sharingId)
          // let link = AppConfig + '/s/' + response.sharingId
          this.shareLink = link
          this.showSharingOptions = true
        }
      } catch (err) {
        console.error(err)
      }
    },
    async callCreateSharesApi(payload) {
      let response
      let getShareResponse = await Shares.getShareByEmail(payload.subject)
      if (getShareResponse.status && getShareResponse.status === 204) {
        response = await Shares.createShare(payload)
      } else {
        let sharingId = get(getShareResponse.data, 'sharingRecords[0].sharingId')
        await Shares.updateShare(sharingId, payload)
        response = get(getShareResponse.data, 'sharingRecords[0]')
      }
      return response
    },
    resetFlow() {
      this.step = 1
      this.form.subject = ''
      this.initialValue = this.hasCancerData ? 'cc' : '*'
      this.form.resourceType = this.documentToShare
        ? this.documentToShare.filename || this.documentToShare.description
        : this.initialValue
      this.showSharingOptions = false
      this.shareLink = ''
      //this.fetchExistingShareLink()
    },
    async createShare(email, type) {
      this.step = 3
      let payload = {
        subject: this.userEmail || this.form.email || email,
        duration: this.form.duration,
        meta: {}
      }
      if (this.documentToShare) {
        payload.id = this.documentToShare.id
        payload.meta.filename = this.documentToShare.filename || this.documentToShare.description
        payload.resourceType = this.documentToShare.resourceType
      } else {
        payload.resourceType = this.form.resourceType
      }
      payload.emailNotify = true
      payload.sharingType = 'external_email'
      let response = await this.callCreateSharesApi(payload)
      if (type !== 'link') this.step = 3
      if (this.documentToShare) {
        Utils.callSegmentTrack({ name: 'pp.shared.clicked', share_scope: 'document' })
      } else if (payload.resourceType === 'cc') {
        Utils.callSegmentTrack({ name: 'pp.share.clicked', share_scope: 'cancer_summary' })
      } else if (payload.resourceType === '*') {
        Utils.callSegmentTrack({ name: 'pp.share.clicked', share_scope: 'entire_profile' })
      }
      Utils.callSegmentTrack({ name: 'pp.create_share.successfully' })
      return response
    },
    handleLinkCreation(val) {
      this.form.resourceType = val
      this.fetchExistingShareLink()
    },
    getShareType(shareType, sharingId) {
      let link = ''
      if (shareType === '*') {
        this.isExistingUser
          ? (link = AppConfig + '/shared/' + sharingId + '/records')
          : (link = AppConfig + '/shared/' + sharingId + '/summary')
      } else if (shareType === 'cc') {
        link = AppConfig + '/s/' + sharingId + '/cancer'
      } else if (shareType === 'ImagingStudy') {
        link = AppConfig + '/sharedRecords/' + sharingId + '/records?viewDicom=true'
      } else {
        // link = AppConfig + '/shared/' + sharingId + '/records'
        link = AppConfig + '/sharedRecords/' + sharingId + '/records'
      }
      localStorage.setItem('shareType', shareType)
      return link
    }
  },
  watch: {
    opened(visible) {
      if (visible) {
        // Here you would put something to happen when dialog opens up
        this.handleModalOpening()
      }
    }
  },
  async mounted() {
    // Only send cancer request when share modal is open
    if (localStorage.getItem('default_auth_token')) {
      const response = await Cancer.fetchCancerData({ ignoreStatus: true })
      if (response.length) {
        this.hasCancerData = true
      }
    }
  }
}
</script>

<style scoped>
.share-modal-header {
  display: flex;
  justify-content: center;
}

.share-modal-title {
  font-style: normal;
  font-weight: 600;
  font-size: 22px;
  line-height: 30px;
  text-align: center;
  color: #161616;
  margin-top: 12px;
}

.document-to-share {
  margin: 20px 0;
  font-style: italic;
  font-size: 14px;
}

.radio-group-wrapper {
  margin-left: 0;
  width: 100%;
}
.radio-btn {
  margin-bottom: 24px !important;
  color: #161616;
}

.v-input--selection-controls__input {
  margin-right: 16px;
}

.v-input--selection-controls .v-radio > .v-label {
  color: #161616 !important;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 27px;
  margin-left: 16px !important;
}

.guest-share-main-wrapper {
  width: 100%;
  display: flex;
  margin: 16px 0;
  height: 48px !important;
  padding: 16px 0;
  align-content: center;
  align-items: center;
}

.done-wrapper {
  display: flex;
  height: 100%;
  margin-bottom: 70px;
}

.guest-share-wrapper {
  width: 100%;
  display: flex;
  height: 48px !important;
  align-items: center;
}

.confirm-share-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: auto;
  margin-top: 15px;
}

.share-values {
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 27px;
  text-align: center;
  color: #000000;
}

.device-sharing-btn {
  background: #1a73eb;
  color: white;
  font-weight: 500;
  border-radius: 4px;
  margin: 0 auto;
  padding: 10px;
  text-align: center;
  font-size: 14px;
  width: 300px;
  cursor: pointer;
  margin-top: 40px;
}

.copied-text {
  color: green;
  font-size: 12px;
  font-weight: 400;
  visibility: hidden;
  text-align: center;
  margin: 5px auto 3px auto;
}

.link-actions-container {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  justify-content: space-evenly;
}

.link-wrapper {
  border: 1px solid #c5c5c5;
  border-radius: 4px;
  display: flex;
  align-items: center;
  width: 400px;
}

.link-box-wrapper {
  padding: 0 10px;
  cursor: pointer;
  width: 90%;
}

.link-box {
  color: #4a4a4a;
  padding: 10px 0;
  word-break: keep-all;
  flex-wrap: nowrap;
  overflow: hidden;
  display: block;
  white-space: nowrap;
}

.link-box::-webkit-scrollbar {
  display: none;
}

.copy-link-text {
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #128279;
  margin: 0 8px;
}

.create-share-btn {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 11px;
  height: 44px;
  border: 2px solid #128279;
  border-radius: 6px;
  flex: none;
  order: 3;
  align-self: stretch;
  flex-grow: 0;
  padding: 16px 0;
  max-width: 390px;
  margin: 24px 0px 40px 0px;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 1px;
  color: #128279;
}

.share-text-main {
  display: flex;
  flex-direction: column;
  align-self: center;
  min-width: 390px;
}

.close-btn-wraper {
  width: 40px;
  height: 40px;
  background-color: #161616;
  display: flex;
  align-content: center;
  align-items: center;
  border-radius: 20px;
}

.copy-link {
  display: flex;
}

.share-confirmation-text {
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 27px;
  color: #161616;
  margin: 16px 16px;
  text-align: center;
  word-break: break-word;
  display: flex;
  flex-direction: column;
}
.shareType-Styling {
  border-radius: 22px;
  font-style: normal;
  font-weight: 450;
  font-size: 16px;
  line-height: 18px;
  margin-top: 8px;
  padding: 7px 16px;
  height: 34px;
  width: fit-content;
  align-self: center;
}

.with-styling {
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 27px;
  color: #161616;
  margin: 12px 0;
  align-self: center;
}

.done-icon-styling {
  align-self: center;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  align-content: center;
  margin: 40px;
}

.cancel-btn-style {
  color: #161616;
  margin-left: 10px;
  border: 1px solid #161616;
  border-radius: 6px;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  width: 167px;
  height: 48px;
  background-color: #ffffff;
  text-align: center;
  padding: 10px 0px;
  margin-right: 10px;
}

.share-btn-style {
  color: #ffffff;
  margin-left: 10px;
  border: 1px solid #ffffff;
  border-radius: 6px;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  width: 167px;
  height: 48px;
  background-color: #128279;
  text-align: center;
  padding: 10px 0px;
  margin-left: 10px;
}

.done-btn-style {
  align-self: flex-end;
  margin-bottom: 70px;
  color: #ffffff;
  border: 1px solid #ffffff;
  border-radius: 6px;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  width: 80%;
  height: 48px;
  background-color: #128279;
  text-align: center;
  padding: 10px 0px;
  margin: 0 10%;
}

.mt {
  padding: 40px 20px 75px !important;
}

.pb {
  padding-bottom: 90px !important;
}

.show {
  visibility: visible;
}
.v-btn {
  text-transform: none !important;
}
.share-wrapper {
  width: 600px;
  background-color: white;
  padding: 20px;
  height: 400px;
}
#app {
  position: inherit;
  width: 600px;
}
@media only screen and (max-width: 571px) {
  .share-wrapper {
    width: 100vw;
    height: calc(100vh - 10px);
  }
  #app {
    position: inherit;
    width: 100vw;
  }
  .share-modal-header {
    margin-top: 20px;
  }
}

@media only screen and (min-width: 572px) {
  #app {
    height: 600px;
  }
  .done-wrapper {
    height: 15%;
    margin-bottom: 50px;
  }
}

@media only screen and (max-width: 471px) and (min-width: 300px) {
  .link-wrapper {
    width: 350px;
  }
}
</style>
